import * as React from "react";
import { Link, Paper } from "@mui/material";
import { useLocation } from "react-router";
import { Box } from "@mui/system";
import { useTranslation } from "react-i18next";

export type DemoTableOfContentsProps = {
  wrapperRef: HTMLDivElement;
};

interface Contents {
  title: string;
  path: string;
  children: Contents[];
}

const DemoTableOfContents: React.FC<DemoTableOfContentsProps> = (props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { wrapperRef } = props;
  const [contents, setContents] = React.useState<Contents[]>([]);
  const [formerPathname, setFormerPathname] = React.useState<string>("");

  const setTableOfContentsPath = React.useCallback(() => {
    const _contents: Contents[] = [];

    [...wrapperRef.querySelectorAll("h1")].forEach((title) => {
      const path = title.getAttribute("id");
      if (!title.textContent || !title.textContent.length || !path || !path.length) return;

      const contentChildren: Contents[] = [];
      const parentDiv = title.closest("div");
      if (parentDiv) {
        [...parentDiv.querySelectorAll("h2")].forEach((subtitle) => {
          const subpath = subtitle.getAttribute("id");
          if (!subtitle.textContent || !subtitle.textContent.length || !subpath || !subpath.length) return;
          contentChildren.push({
            title: subtitle.textContent,
            path: subpath,
            children: [],
          });
        });
      }

      _contents.push({
        title: title.textContent,
        path: title.getAttribute("id") || "",
        children: contentChildren,
      });
    });

    setContents(_contents);
  }, [wrapperRef]);

  React.useEffect(() => {
    setTableOfContentsPath();
  }, [setTableOfContentsPath]);

  React.useEffect(() => {
    if (formerPathname !== location.pathname) {
      setFormerPathname(location.pathname);
      setTableOfContentsPath();
    }
  }, [setTableOfContentsPath, location, formerPathname]);

  const onClickPath: React.MouseEventHandler<HTMLAnchorElement> = React.useCallback((e) => {
    if (e) {
      e.preventDefault();
    }

    const target = document.querySelector(`${e.currentTarget.getAttribute("href")?.slice(0)}`);

    if (!target) return;
    const rect = target.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const top = rect.top + scrollTop - 100;

    const scrollableContent = document.querySelector(".dashboard-content");
    if (!scrollableContent) return;
    scrollableContent.scrollTo(0, top);
  }, []);

  return (
    <Paper sx={{ p: 3, mb: 4 }}>
      <h1>{t("component:paper.tableOfContents")}</h1>
      <ul>
        {contents.map((content, i) => (
          <Box component="li" mb={1} key={i}>
            <Link variant="subtitle2" href={`#${content.path}`} onClick={onClickPath}>
              {content.title}
            </Link>
            <Box sx={{ ml: 3 }} component="ul">
              {content.children.map((subcontent, i) => (
                <li key={i}>
                  <Link href={`#${subcontent.path}`} onClick={onClickPath}>
                    {subcontent.title}
                  </Link>
                </li>
              ))}
            </Box>
          </Box>
        ))}
      </ul>
    </Paper>
  );
};

export default DemoTableOfContents;
